import * as React from "react"
import { ScrollArea as ScrollAreaPrimitive } from "radix-ui"

import { cn } from "@client/lib/utils"

function ScrollArea({
  className,
  children,
  viewportRef,
  viewportClassName,
  viewportProps,
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  /**
   * The node that actually scrolls. Radix keeps it inside the root, so anything
   * that reads or drives scroll position — auto-following a streaming answer,
   * for instance — needs it handed out explicitly.
   */
  viewportRef?: React.Ref<HTMLDivElement>
  viewportClassName?: string
  /**
   * Attributes for that same node. A scroll container that a keyboard user is
   * meant to reach needs `tabIndex` and a name on the *viewport*, not on the
   * root — the root does not scroll, so focusing it does nothing.
   */
  viewportProps?: React.ComponentProps<typeof ScrollAreaPrimitive.Viewport>
}) {
  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={cn("relative", className)}
      {...props}
    >
      {/*
        `[&>div]:block!` overrides the `display: table` Radix puts on its content
        wrapper as an inline style. A table box is sized by its content, so it
        grows past the viewport instead of filling it — which quietly breaks
        every `truncate` inside and pushes trailing controls out of the clipped
        region. Block keeps Radix's `min-width: 100%` and drops the growth.
      */}
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        data-slot="scroll-area-viewport"
        {...viewportProps}
        className={cn(
          "size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 [&>div]:block!",
          viewportClassName
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
}

function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none p-px transition-colors select-none",
        orientation === "vertical" &&
          "h-full w-2.5 border-l border-l-transparent",
        orientation === "horizontal" &&
          "h-2.5 flex-col border-t border-t-transparent",
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="relative flex-1 rounded-full bg-border"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollArea, ScrollBar }
